import { CloseOutlined } from '@ant-design/icons';
import { default as Collapse } from 'antd/es/collapse';
import type { CollapseProps } from 'antd/es/collapse';
import { default as Skeleton } from 'antd/es/skeleton';
import type { Dispatch } from 'react';
import { useMemo } from 'react';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import type { WithdrawalReasonCode, WithdrawalRequests } from 'api';
import { useGetDisbursementWithdrawalRequestsWithdrawalRequestId } from 'api';

import { useOrganisationId } from 'hooks/context';

import { Badge } from '@atoms/badges/Badge/Badge';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { DetailsItem } from '@atoms/DetailsItem/DetailsItem';
import { TextEllipsisMiddle } from '@atoms/TextEllipsisMiddle';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';

import { WITHDRAWAL_DETAILS_TEST_ID } from './constants';
import { getWithdrawalSummary } from './get-withdrawal-summary';
import { WithdrawalDetailsHistory } from './WithdrawalDetailsHistory';

type Props = {
  selectedRowData: WithdrawalRequests;
  onClose: Dispatch<unknown>;
};

export const WithdrawalDetails = ({ selectedRowData, onClose }: Props) => {
  const organisationId = useOrganisationId<number>();

  const {
    data: { data: withdrawalRequestFullData } = {},
    isLoading: isWithdrawalRequestLoading,
    isSuccess: isWithdrawalRequestSuccess
  } = useGetDisbursementWithdrawalRequestsWithdrawalRequestId(
    organisationId,
    Number(selectedRowData.id)
  );

  const reasonCode: WithdrawalReasonCode | undefined = useMemo(() => {
    if (
      withdrawalRequestFullData &&
      withdrawalRequestFullData.status === 'rejected'
    ) {
      return withdrawalRequestFullData.status_transitions.reduce(
        (latest, current) => {
          return new Date(current.created_at) > new Date(latest.created_at)
            ? current
            : latest;
        },
        withdrawalRequestFullData.status_transitions[0]
      )?.reason_code;
    }
  }, [withdrawalRequestFullData]);

  const items: CollapseProps['items'] = useMemo(() => {
    if (!withdrawalRequestFullData || !isWithdrawalRequestSuccess) {
      return [];
    }

    const { creditor_account: creditorAccount } = withdrawalRequestFullData;

    const sections: CollapseProps['items'] = [];

    // If rejected, push the summary section
    if (withdrawalRequestFullData.status === 'rejected') {
      sections.push({
        key: 'Summary',
        label: 'Summary',
        children: (
          <div>
            {isWithdrawalRequestLoading ? (
              <Skeleton />
            ) : (
              <div>
                <div className="mb-4">
                  {reasonCode ? (
                    <div className="flex flex-wrap gap-2">
                      <Badge
                        label={reasonCode.external_message}
                        className="bg-status-negative"
                      />
                    </div>
                  ) : (
                    <Badge
                      key="unknown"
                      label="Unknown"
                      className="bg-feedback-error-surface"
                    />
                  )}
                </div>
                {getWithdrawalSummary(withdrawalRequestFullData.status)}
              </div>
            )}
          </div>
        )
      });
    }

    sections.push(
      {
        key: 'Funds withdrawal',
        label: 'Funds withdrawal',
        children: (
          <div>
            {isWithdrawalRequestLoading ? (
              <Skeleton />
            ) : (
              <div>
                <DetailsItem
                  title="Reference"
                  item={
                    <CopyToClipboardButton
                      text={selectedRowData.reference?.toLocaleUpperCase()}
                    />
                  }
                />
                <DetailsItem
                  title="Amount"
                  item={`${formatDecimalAmount(Number(selectedRowData.amount))} ${selectedRowData.currency_code}`}
                />
                <DetailsItem title="Bank" item={creditorAccount?.bank_name} />
                <DetailsItem
                  title="Network"
                  item={creditorAccount?.bank_clearing_system_id}
                />
                {creditorAccount?.iban && (
                  <DetailsItem
                    title="IBAN"
                    item={<CopyToClipboardButton text={creditorAccount.iban} />}
                  />
                )}
                {creditorAccount?.account_number && (
                  <DetailsItem
                    title={
                      creditorAccount.local_instrument !== 'CRYPTO'
                        ? 'Account number'
                        : 'Wallet address'
                    }
                    item={
                      <CopyToClipboardButton
                        text={creditorAccount.account_number}
                        displayText={
                          <TextEllipsisMiddle
                            text={creditorAccount.account_number}
                            maxLength={20}
                          />
                        }
                      />
                    }
                  />
                )}
                <DetailsItem
                  title="Beneficiary"
                  item={
                    Boolean(creditorAccount?.party_identification?.name)
                      ? creditorAccount?.party_identification?.name
                      : 'Information not found'
                  }
                />
              </div>
            )}
          </div>
        )
      },
      {
        key: 'History',
        label: 'History',
        children: (
          <div>
            {withdrawalRequestFullData ? (
              <WithdrawalDetailsHistory
                withdrawalRequestFullData={withdrawalRequestFullData}
              />
            ) : (
              <Skeleton />
            )}
          </div>
        )
      }
    );
    return sections;
  }, [
    isWithdrawalRequestLoading,
    isWithdrawalRequestSuccess,
    reasonCode,
    selectedRowData.amount,
    selectedRowData.currency_code,
    selectedRowData.reference,
    withdrawalRequestFullData
  ]);

  return (
    <div
      className="cool-shadow bg-white"
      style={{ flex: 0.25, borderRadius: '.75rem' }}
      data-testid={WITHDRAWAL_DETAILS_TEST_ID}
    >
      <div className="flex justify-between rounded-t-xl bg-white px-6 py-3.5 text-xl">
        Details
        <TertiaryButton icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="border-y bg-surface-selected px-3 py-2 text-sm">
        Account Information:{' '}
        <strong>
          {selectedRowData.currency_code}{' '}
          {selectedRowData.debtor_account_number}
        </strong>
      </div>
      <Collapse
        className="antd-details-collapse bg-white"
        expandIconPosition="end"
        accordion
        bordered={false}
        defaultActiveKey={[
          hasValue(reasonCode) ? 'Summary' : 'Funds withdrawal'
        ]}
        key={hasValue(reasonCode) ? 'Summary' : 'Funds withdrawal'}
        items={items}
      />
    </div>
  );
};
